import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import "./UnlockConfetti.css";

/**
 * Detect new drip-unlock events and fire a confetti burst + banner.
 * `events` should be the array of unlock events (newest first). The component
 * remembers the last event id it saw in localStorage so it only celebrates the
 * truly NEW unlocks on each session.
 */
const SEEN_KEY = "cauldron_last_unlock_seen";

export default function UnlockConfetti({ events }) {
  const [active, setActive] = useState(null); // { totalAmount, count }

  useEffect(() => {
    if (!Array.isArray(events) || events.length === 0) return;
    const lastSeen = localStorage.getItem(SEEN_KEY);
    const newestId = events[0]?.id;
    if (lastSeen === newestId) return;

    // Find every event newer than lastSeen (or all if first time)
    let cutIdx = events.findIndex((e) => e.id === lastSeen);
    if (cutIdx < 0) cutIdx = Math.min(events.length, 3); // first visit: celebrate top 3 max
    const fresh = events.slice(0, cutIdx);
    if (fresh.length === 0) {
      localStorage.setItem(SEEN_KEY, newestId);
      return;
    }
    const totalAmount = fresh.reduce((s, e) => s + (e.amount || 0), 0);
    setActive({ totalAmount, count: fresh.length });
    localStorage.setItem(SEEN_KEY, newestId);
    const t = setTimeout(() => setActive(null), 3500);
    return () => clearTimeout(t);
  }, [events]);

  if (!active) return null;

  // Render 40 confetti pieces with randomised left + delay
  const pieces = Array.from({ length: 40 }, (_, i) => {
    const left = Math.random() * 100;
    const delay = Math.random() * 0.6;
    const dx = (Math.random() - 0.5) * 200;
    return (
      <span
        key={i}
        className="confetti-piece"
        style={{
          left: `${left}%`,
          animationDelay: `${delay}s`,
          "--dx": `${dx}px`,
        }}
      />
    );
  });

  return (
    <>
      <div className="confetti-burst" data-testid="unlock-confetti">{pieces}</div>
      <div className="unlock-banner" data-testid="unlock-banner">
        <Sparkles size={18} strokeWidth={1.75} />
        <span>+{active.totalAmount} CAUL unlocked {active.count > 1 ? `· ${active.count} grants` : ""}</span>
      </div>
    </>
  );
}
